import { useTranslation } from "@i18n";
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import ModalStateDialogComponent from "@CubeComponents/html/modal/ModalWithState";
import React, { useEffect, useState } from "react";
import GoogleMap from "@Components/Map";
import IconComponent from "@CubeComponents/icons";
import type { QuestionField } from "@ServerTypes/forms/forms.schema";

/** A map position, in the `{ lat, lng }` shape `getDistanceInMeters` and `GoogleMap` both expect. */
interface GeoPosition {
  lat: number;
  lng: number;
}

/** What this modal holds and hands to `handleSaveLocation` — the geoLocation answer's stored shape. */
interface GeoLocationSelection {
  current: GeoPosition | null;
  selected: GeoPosition | null;
  mapZoomLevel: number;
}

/**
 * What `GoogleMap` reports through `getLocationCallBack`. The positions are optional here (and
 * defaulted to null below) because the map fires the callback before either has been resolved;
 * `GoogleMap` also passes a `selectedPinAddress` this modal ignores.
 */
interface GeoLocationCallbackArgs {
  current?: GeoPosition | null;
  selected?: GeoPosition | null;
  mapZoomLevel: number;
}

interface AddGeoLocationProps {
  setShowGeoLocationModal: (state: { state: boolean }) => void;
  /**
   * `eachField` is required, not optional: `footerContent` reads
   * `showGeoLocationModal.eachField.allowManualSelection` with no guard. The only caller
   * (questionFields) renders this modal solely from `handleGeoLocationModalState`, which always
   * sets it, so the contract is "give me the field" rather than a latent crash.
   */
  showGeoLocationModal: {
    state: boolean;
    uniqueId?: string;
    eachField: Pick<QuestionField, "allowManualSelection" | "captureLiveLocation">;
  };
  handleSaveLocation: (location: GeoLocationSelection) => void;
  name: string | number;
}

const AddGeoLocation = ({
  setShowGeoLocationModal,
  showGeoLocationModal,
  handleSaveLocation,
  name,
}: AddGeoLocationProps) => {
  const { t } = useTranslation();
  const [locationData, setLocationData] = useState<GeoLocationSelection>({
    current: null,
    selected: null,
    mapZoomLevel: 14,
  });
  const [isLocationDenied, setIsLocationDenied] = useState(false);

  const handleCancelClick = () => {
    setShowGeoLocationModal({ state: false });
  };

  const handleSaveClick = () => {
    handleSaveLocation(locationData);
  };

  const getLocationCallBack = ({ current = null, selected = null, mapZoomLevel }: GeoLocationCallbackArgs) => {
    if (isLocationDenied || !showGeoLocationModal?.eachField?.captureLiveLocation) {
      current = null;
    }
    setLocationData({ current, selected, mapZoomLevel });
  };

  useEffect(() => {
    if (!navigator.permissions) {
      console.log("Permissions API not supported");
      return;
    }

    navigator.permissions.query({ name: "geolocation" }).then((result: PermissionStatus) => {
      if (result.state === "granted") {
        setIsLocationDenied(false);
      } else if (result.state === "denied") {
        setIsLocationDenied(true);
      } else {
        setIsLocationDenied(false);
      }
    });
  }, []);

  const modelContent = () => {
    const allowManual = !!showGeoLocationModal?.eachField?.allowManualSelection;
    if (isLocationDenied && !allowManual) {
      return (
        <div className="flex h-full w-full flex-col items-center justify-center gap-3">
          <IconComponent name="locationDenied" width="15rem" height="15.0625rem" />
          <span className="text-2xl font-semibold">{t("Location permission not enabled ! Check Site Settings.")}</span>
          <span>
            {t(
              "Since this field does not allow manual selection of location, enabling permissions for location is mandatory to move forward.",
            )}
          </span>
        </div>
      );
    }

    return (
      <div className="flex h-full w-full flex-col gap-5">
        {!allowManual && (
          <div className="flex h-12 w-fit items-center gap-2 rounded-lg bg-system_colour_12 bg-opacity-30 ps-3 pe-3">
            {t("This field does not allow manual selection of location")}
          </div>
        )}
        <GoogleMap
          allowManualSelection={allowManual}
          allowEditing
          allowSearch={allowManual}
          showLiveLocation={!isLocationDenied && !!showGeoLocationModal?.eachField?.captureLiveLocation}
          autoFetchLiveLocation={!isLocationDenied}
          // @ts-expect-error -- the map callback is declared with a required destructured arg while the child invokes it with no argument on cancel.
          getLocationCallBack={getLocationCallBack}
        />
      </div>
    );
  };

  const footerContent = () =>
    isLocationDenied && !showGeoLocationModal.eachField.allowManualSelection ? undefined : (
      <div className="flex gap-4 w-full">
        <ButtonComponent onClick={handleSaveClick} buttonType={BUTTON_TYPES.GRADIENT_BUTTON}>
          {t("Save")}
        </ButtonComponent>
        <ButtonComponent onClick={handleCancelClick} buttonType={BUTTON_TYPES.BACKGROUND_BUTTON}>
          {t("Cancel")}
        </ButtonComponent>
      </div>
    );

  return (
    <ModalStateDialogComponent
      headerTitle={<div className="flex justify-between w-full">{t("Select Location")}</div>}
      modelContent={modelContent()}
      onCancel={handleCancelClick}
      footerContent={footerContent()}
      width="80%"
      height="90%"
      isVisible={showGeoLocationModal.state && name == showGeoLocationModal.uniqueId}
    />
  );
};
export default AddGeoLocation;
